import React, { ComponentProps, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { default as Form } from '../../components/connectedForm';
import * as input from '../../components/connectedForm/inputComponents';
import HeaderContainer from '../../components/connectedForm/HeaderContainer';
import HeaderTitle from '../../components/connectedForm/HeaderTitle';
import HeaderAction from '../../components/connectedForm/HeaderAction';
import { Button } from '../../components/connectedForm/Button';
import SalesInvoiceTransactionQuery from '../../graphql/queries/salesLedger/SalesInvoiceTransactionQuery.graphql';
import SalesCreditTransactionQuery from '../../graphql/queries/salesLedger/SalesCreditTransactionQuery.graphql';
import SalesReceiptTransactionQuery from '../../graphql/queries/salesLedger/SalesReceiptTransactionQuery.graphql';
import SalesRefundTransactionQuery from '../../graphql/queries/salesLedger/SalesRefundTransactionQuery.graphql';
import SalesBadDebtTransactionQuery from '../../graphql/queries/salesLedger/SalesBadDebtTransactionQuery.graphql';
import CustomerOverdueBalanceQuery from '../../graphql/queries/salesLedger/CustomerOverdueBalanceQuery.graphql';
import SalesTransactionAllocationQuery from '../../graphql/queries/salesLedger/SalesTransactionAllocationQuery.graphql';
import SalesVatOnlyTransactionQuery from '../../graphql/queries/salesLedger/SalesVatOnlyTransactionQuery.graphql';
import SalesLedgerJournalTransactionQuery from '../../graphql/queries/salesLedger/SalesLedgerJournalTransactionQuery.graphql';
import ValueCard from '../../components/connectedForm/ValueCard';
import { Customer } from '../../graphql/entities';
import SalesTransactionWorkspace from '../../views/salesLedger/SalesTransactionWorkspace';
import ValueBalanceBar from '../../components/ValueBalanceBar/ValueBalanceBar';
import styled from 'styled-components';
import * as DateHelper from '../../utils/DateHelper';
import HeaderStatus from '../../components/connectedForm/HeaderStatus';
import moment from 'moment';
import EditableDataGrid from '../../components/EditableDataGrid/EditableDataGrid';
import { useDimensionColumns } from '../../utils/DimensionSets';
import Formatters from '../../components/grid-addons/formatters';
import getDocumentModalProps from '../../utils/getDocumentModalProps';
import GridContainer from '../../components/connectedForm/GridContainer';
import Spacer from '../../components/Spacer';
import CurrencyContext from '../../components/Context/CurrencyContext';
import DynamicReportContext from '../../components/Context/DynamicReportContext';

import {
  DocumentType,
  FilterOperation,
  ICustomer,
  IFilterInput,
  IOverdueBalance,
  IPostedTransactionsResponse,
  IPurchaseAllocationLine,
  IPurchaseTransaction,
  ISalesAllocationLine,
  ISalesBadDebtTransactionsQueryResult,
  ISalesBadDebtTransactionsQueryVariables,
  ISalesCreditTransactionsQueryResult,
  ISalesCreditTransactionsQueryVariables,
  ISalesInvoiceTransactionsQueryResult,
  ISalesInvoiceTransactionsQueryVariables,
  ISalesLedgerJournalTransactionsQueryResult,
  ISalesLedgerJournalTransactionsQueryVariables,
  ISalesOverdueBalanceQueryResult,
  ISalesOverdueBalanceQueryVariables,
  ISalesReceiptTransactionsQueryResult,
  ISalesReceiptTransactionsQueryVariables,
  ISalesRefundTransactionsQueryResult,
  ISalesRefundTransactionsQueryVariables,
  ISalesTransaction,
  ISalesTransactionAllocationsQueryResult,
  ISalesTransactionAllocationsQueryVariables,
  ISalesVatOnlyTransactionsQueryResult,
  ISalesVatOnlyTransactionsQueryVariables,
  Maybe,
} from '../../graphql/schema/p4types';
import { useValueState } from '../../utils/hooks';
import { QueryTuple, useLazyQuery, useQuery } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { useHistory } from 'react-router';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import BreadCrumbs from '../../components/connectedForm/BreadCrumbs';
import useWorkspaceGenericReportAction from '../../actions/useDynamicReportAction';
import { PrimaryButton } from '@fluentui/react';
import { GuidedTour } from '../../components/Teaching/components/GuidedTour/GuidedTour';
import { TabButton } from '../../components/connectedForm/TabButton/TabButton';
import { OperationVariables } from '@apollo/client/core';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import { Tooltip } from '@fluentui/react-components';

const LazySalesAllocationModal = React.lazy(() => import('../../components/modals/SalesAllocationModal'));

const TransactionHeader = styled.div`
  display: inline-block;
  font-size: 17px;
  font-weight: 600;
  padding-right: 10px;
  color: #3949ab;
`;

const SubHeader = styled.div`
  display: inline-block;
  font-size: 12px;
  font-weight: 400;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.54);
  padding-right: 30px;
`;

const nominalEmptyRowsView = () => <EmptyRows type={'Nominal'} />;
const vatEmptyRowsView = () => <EmptyRows type={'VAT'} />;
const allocationEmptyRowsView = () => <EmptyRows type={'Allocation'} />;

export const TransactionEnquiryHeaderBlock = ({
  onCancel,
  title = 'Sales Ledger',
  value = 'Sales Transaction Enquiry',
}: {
  onCancel: () => void;
  title?: string;
  value?: string;
}) => {
  return (
    <>
      <BreadCrumbs title="Transaction Enquiry" />
      <HeaderContainer>
        <HeaderTitle title={title} value={value} optValue={null} />
        <HeaderAction>
          <Button onClick={onCancel}>Cancel</Button>
        </HeaderAction>
      </HeaderContainer>
    </>
  );
};

export const getTransactionEnquiryGridHeight = (type: 'main' | 'sub', embedded: boolean, heightIncrease?: number) => {
  const height = window.innerHeight - (embedded ? 450 : 300);
  const scaledHeight = type === 'main' ? Math.floor(height * 0.6) : Math.max(180, Math.floor(height * 0.3));
  return scaledHeight + (heightIncrease ?? 0);
};

export const getTransactionEnquiryCurrencyIcon = (balance: Maybe<IOverdueBalance>) => {
  if (!balance || !balance.foreignCurrency) return null;
  return (
    <HeaderStatus
      icon="account_balance"
      key="currency_status"
      statusStyle="info"
      title={`${balance.currency?.isoCode?.codeAlpha3}`}
      tooltip={'Currency code for this Customer.'}
    />
  );
};

export const getTransactionEnquiryAccountStatusIcon = (balance: Maybe<IOverdueBalance>) => {
  if (!balance || !balance.onStop) return null;
  return (
    <HeaderStatus
      icon={'stop'}
      key="account_status"
      statusStyle="error"
      title="ON STOP"
      tooltip={'This Customer is on stop.'}
    />
  );
};

const EmbeddedDynamicReport = () => {
  const [genericReportAction, genericReportModal] = useWorkspaceGenericReportAction();
  const { disabled, tooltip } = genericReportAction;

  /** DIV wrapper required for tooltip. Do not remove. **/
  const button = (
    <div>
      <PrimaryButton disabled={disabled} onClick={genericReportAction.onClick as () => void}>
        {genericReportAction.text}
      </PrimaryButton>
    </div>
  );

  return (
    <>
      {genericReportModal}
      {tooltip ? (
        <Tooltip content={tooltip} relationship={'label'}>
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </>
  );
};

export const TRANSACTION_ENQUIRY_DIMENSION_COLUMN_META: Array<IEditableDataGridColumnDef> = [
  {
    name: 'Nominal',
    key: 'nominal.accountNo',
    width: 125,
    resizable: true,
  },
  {
    name: 'Description',
    key: 'nominal.name',
    resizable: true,
  },
  {
    name: 'Bank',
    key: 'nominal.bankAccount',
    width: 50,
    formatter: Formatters.BooleanFormatter,
    resizable: false,
  },
  {
    name: 'Type',
    key: 'nominal.accountTypeDisplay',
    width: 150,
    resizable: true,
  },
  {
    name: 'Financial Period',
    key: 'financialPeriod.name',
    resizable: true,
  },
  {
    name: 'Debit',
    key: 'baseDebitAmount',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
    cellClass: 'rightAlign',
    width: 150,
  },
  {
    name: 'Credit',
    key: 'baseCreditAmount',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
    cellClass: 'rightAlign',
    width: 150,
  },
];

export const TRANSACTION_ENQUIRY_VAT_COLUMN_META = [
  {
    name: 'Financial Period',
    key: 'financialPeriod.name',
    resizable: true,
  },
  {
    name: 'Authority',
    key: 'vatAuthority.description',
    resizable: true,
  },
  {
    name: 'Business VAT Status',
    key: 'vatBusinessCategory.description',
    resizable: true,
  },
  {
    name: 'Product VAT Category',
    key: 'vatProductCategory.description',
    resizable: true,
  },
  {
    name: 'Rate',
    key: 'rate',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2, suffix: '%' },
    },
    cellClass: 'rightAlign',
    width: 100,
  },
  {
    name: 'Goods',
    key: 'baseGoods',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
    cellClass: 'rightAlign',
    width: 150,
  },
  {
    name: 'VAT',
    key: 'baseVat',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
    cellClass: 'rightAlign',
    width: 150,
  },
];

const ALLOCATION_COLUMN_META = [
  {
    name: 'Allocation Date',
    key: 'salesAllocation.documentDate',
    formatter: Formatters.DateFormatter,
    resizable: true,
  },
  {
    name: 'Document No',
    key: 'salesAllocation.documentNumber',
    resizable: true,
    formatter: ModalFormatter,
    options: {
      modalContent: LazySalesAllocationModal,
      entityId: 'salesAllocation.id',
      modalProps: getDocumentModalProps,
    },
  },
  {
    name: 'Total Allocations In Batch',
    key: 'salesAllocation.debitTotal',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
    cellClass: 'rightAlign',
  },
  {
    name: 'Allocated Amount',
    key: 'allocatedTotal',
    resizable: true,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
    cellClass: 'rightAlign',
  },
];

export const getBalanceColors = (balance: IOverdueBalance) => {
  const { overdueAmount, outstandingAmount, creditLimit } = balance;
  const creditPercent =
    creditLimit > 0 && outstandingAmount > 0 ? Math.floor((outstandingAmount / creditLimit) * 100.0) : 0;
  let outstandingColour;
  if (outstandingAmount < 0) {
    outstandingColour = '#4caf50';
  } else {
    outstandingColour = outstandingAmount >= creditLimit && creditLimit > 0 ? '#ffa000' : null;
  }
  let creditColour;
  if (creditPercent > 70) {
    creditColour = '#f44336';
  } else {
    creditColour = creditPercent > 40 ? '#ffa000' : '#4caf50';
  }

  return {
    overdue: overdueAmount ? '#f44336' : null,
    outstanding: outstandingColour,
    barColor: outstandingAmount < 0 ? '#4caf50' : '#ffa000',
    barProgressColor: '#f44336',
    overduePercent: overdueAmount > 0 ? Math.floor((overdueAmount / outstandingAmount) * 100.0) : 0,
    creditPercent,
    creditColor: creditColour,
  };
};

export const setTransactionEnquiryStatus = (
  transaction: ISalesTransaction | IPurchaseTransaction,
  documentType = DocumentType.SalesInvoice
): ComponentProps<typeof HeaderStatus> & { overdueDays?: number } => {
  let tooltip: string;
  let status: string;
  let icon: string;
  if (transaction.outstandingAmount !== 0) {
    if (transaction.allocatedAmount !== 0) {
      status = 'PART ALLOCATED';
      icon = 'radio_button_checked';
      tooltip = `This transaction has been partly allocated, and has ${transaction.outstandingAmount} outstanding.`;
    } else {
      status = 'OUTSTANDING';
      icon = 'radio_button_unchecked';
      tooltip = `This transaction has ${transaction.outstandingAmount} outstanding.`;
    }
  } else {
    status = 'ALLOCATED';
    icon = 'check_circle';
    tooltip = 'This transaction has been fully allocated';
  }
  if (transaction.importedTransaction) status = 'IMPORTED';

  const statusStyle = status === 'ALLOCATED' ? 'success' : 'warning';

  let overdueDays;
  if (status !== 'ALLOCATED' && transaction.documentType === documentType) {
    const dueDate = moment(transaction.paymentDueDate);
    const todaysDate = moment();
    overdueDays = todaysDate.diff(dueDate, 'days');
  }
  return {
    title: status,
    icon,
    statusStyle,
    overdueDays,
    tooltip,
  };
};

export const renderTransactionEnquiryHeader = (
  transaction: ISalesTransaction | IPurchaseTransaction,
  documentType = DocumentType.SalesInvoice
) => {
  const type = transaction.documentType!.replace('_', ' ');
  const created = DateHelper.createDate(transaction.createdAt);
  const formattedDate = created.isValid() ? created.format('ddd Do MMM yyyy HH:mm') : null;
  const { overdueDays, ...statusProps } = setTransactionEnquiryStatus(transaction, documentType);
  return (
    <Form.Fieldset cols={1} collapsed={true}>
      <TransactionHeader>
        {type} - {transaction.documentNumber}
      </TransactionHeader>
      <SubHeader>
        (Created by {transaction.createdBy} on {formattedDate})
      </SubHeader>
      <HeaderStatus {...statusProps} />
      {overdueDays !== undefined && overdueDays > 0 ? (
        <div style={{ marginLeft: '30px', display: 'inline-block' }}>
          <HeaderStatus
            statusStyle="error"
            title={`${overdueDays} DAYS OVERDUE`}
            tooltip={`This transaction is ${overdueDays} days overdue.`}
            icon={'timer'}
          />
        </div>
      ) : null}
    </Form.Fieldset>
  );
};

const EmptyRows = ({ type }: { type: 'Nominal' | 'VAT' | 'Allocation' }) => {
  return (
    <div className="no-results">
      <p style={{ textAlign: 'center' }}>{`No linked ${type} transactions`}</p>
    </div>
  );
};

export const EnquiryNominalTransactions = ({
  transaction,
  nominalVat,
  documentType = DocumentType.SalesInvoice,
  embedded,
}: {
  transaction: Maybe<ISalesTransaction | IPurchaseTransaction>;
  nominalVat: IPostedTransactionsResponse;
  documentType?: DocumentType.SalesInvoice | DocumentType.PurchaseInvoice;
  embedded: boolean;
}) => {
  const columns = useDimensionColumns(TRANSACTION_ENQUIRY_DIMENSION_COLUMN_META);

  if (!transaction || !nominalVat) return null;
  return (
    <>
      {renderTransactionEnquiryHeader(transaction, documentType)}
      <GridContainer alwaysActive={true}>
        <EditableDataGrid
          listData={nominalVat.nominalTransactions || []}
          rowsCount={nominalVat.nominalTransactions?.length ?? 0}
          className="entryGrid"
          columns={columns}
          enableCellSelect={false}
          addBlankRow={false}
          emptyRowsView={nominalEmptyRowsView}
          contentHeight={getTransactionEnquiryGridHeight('sub', embedded)}
          ignoreContainerWidth={true}
        />
      </GridContainer>
    </>
  );
};

export const EnquiryVatTransactions = ({
  transaction,
  nominalVat,
  documentType = DocumentType.SalesInvoice,
  embedded,
}: {
  transaction: Maybe<ISalesTransaction | IPurchaseTransaction>;
  nominalVat: IPostedTransactionsResponse;
  documentType?: DocumentType.SalesInvoice | DocumentType.PurchaseInvoice;
  embedded: boolean;
}) => {
  if (!transaction || !nominalVat) return null;
  return (
    <>
      {renderTransactionEnquiryHeader(transaction, documentType)}
      <GridContainer alwaysActive={true}>
        <EditableDataGrid
          listData={nominalVat.vatTransactions || []}
          rowsCount={nominalVat.vatTransactions?.length ?? 0}
          className="entryGrid"
          columns={TRANSACTION_ENQUIRY_VAT_COLUMN_META}
          enableCellSelect={false}
          addBlankRow={false}
          emptyRowsView={vatEmptyRowsView}
          contentHeight={getTransactionEnquiryGridHeight('sub', embedded)}
          ignoreContainerWidth={true}
        />
      </GridContainer>
    </>
  );
};

export const EnquiryAllocationTransactions = ({
  transaction,
  allocation,
  documentType = DocumentType.SalesInvoice,
  embedded,
  columnMeta = ALLOCATION_COLUMN_META,
}: {
  transaction: Maybe<ISalesTransaction | IPurchaseTransaction>;
  allocation: Array<ISalesAllocationLine> | Array<IPurchaseAllocationLine>;
  documentType?: DocumentType.SalesInvoice | DocumentType.PurchaseInvoice;
  embedded: boolean;
  columnMeta?: Array<IEditableDataGridColumnDef>;
}) => {
  if (!transaction || !allocation) return null;
  return (
    <>
      {renderTransactionEnquiryHeader(transaction, documentType)}
      <GridContainer alwaysActive={true}>
        <EditableDataGrid
          listData={allocation}
          rowsCount={allocation.length}
          className="entryGrid"
          columns={columnMeta}
          enableCellSelect={false}
          addBlankRow={false}
          emptyRowsView={allocationEmptyRowsView}
          contentHeight={getTransactionEnquiryGridHeight('sub', embedded)}
          ignoreContainerWidth={true}
        />
      </GridContainer>
    </>
  );
};

const useNominalVatLazyQuery = <TData, TVariables extends OperationVariables>(
  query: DocumentNode,
  setNominalVat: (data: IPostedTransactionsResponse) => void
): QueryTuple<TData, TVariables> => {
  const [transactionQuery, queryTuple] = useLazyQuery<TData, TVariables>(query, {
    fetchPolicy: 'no-cache',
  });
  const { data } = queryTuple;

  useEffect(() => {
    if (data) {
      setNominalVat((data as any).data as IPostedTransactionsResponse);
    }
  }, [data, setNominalVat]);

  return [transactionQuery, queryTuple];
};

const useNominalVatState = (
  transaction: Maybe<ISalesTransaction>
): [IPostedTransactionsResponse, Dispatch<SetStateAction<IPostedTransactionsResponse>>] => {
  const [nominalVat, setNominalVat] = useState<IPostedTransactionsResponse>({
    nominalTransactions: [],
    vatTransactions: [],
  });

  const [salesInvoiceTransactionQuery] = useNominalVatLazyQuery<
    ISalesInvoiceTransactionsQueryResult,
    ISalesInvoiceTransactionsQueryVariables
  >(SalesInvoiceTransactionQuery, setNominalVat);
  const [salesCreditTransactionQuery] = useNominalVatLazyQuery<
    ISalesCreditTransactionsQueryResult,
    ISalesCreditTransactionsQueryVariables
  >(SalesCreditTransactionQuery, setNominalVat);
  const [salesReceiptTransactionQuery] = useNominalVatLazyQuery<
    ISalesReceiptTransactionsQueryResult,
    ISalesReceiptTransactionsQueryVariables
  >(SalesReceiptTransactionQuery, setNominalVat);
  const [salesRefundTransactionQuery] = useNominalVatLazyQuery<
    ISalesRefundTransactionsQueryResult,
    ISalesRefundTransactionsQueryVariables
  >(SalesRefundTransactionQuery, setNominalVat);
  const [salesBadDebtTransactionQuery] = useNominalVatLazyQuery<
    ISalesBadDebtTransactionsQueryResult,
    ISalesBadDebtTransactionsQueryVariables
  >(SalesBadDebtTransactionQuery, setNominalVat);
  const [salesVatOnlyTransactionQuery] = useNominalVatLazyQuery<
    ISalesVatOnlyTransactionsQueryResult,
    ISalesVatOnlyTransactionsQueryVariables
  >(SalesVatOnlyTransactionQuery, setNominalVat);
  const [salesLedgerJournalTransactionQuery] = useNominalVatLazyQuery<
    ISalesLedgerJournalTransactionsQueryResult,
    ISalesLedgerJournalTransactionsQueryVariables
  >(SalesLedgerJournalTransactionQuery, setNominalVat);

  useEffect(() => {
    if (transaction?.sourceId) {
      switch (transaction?.documentType) {
        case DocumentType.SalesInvoice:
          salesInvoiceTransactionQuery({ variables: { id: transaction.sourceId } });
          break;
        case DocumentType.SalesCredit:
          salesCreditTransactionQuery({ variables: { id: transaction.sourceId } });
          break;
        case DocumentType.SalesReceipt:
          salesReceiptTransactionQuery({ variables: { id: transaction.sourceId } });
          break;
        case DocumentType.SalesRefund:
          salesRefundTransactionQuery({ variables: { id: transaction.sourceId } });
          break;
        case DocumentType.SalesBadDebt:
          salesBadDebtTransactionQuery({ variables: { id: transaction.sourceId } });
          break;
        case DocumentType.SalesVatInvoice:
        case DocumentType.SalesVatCredit:
          salesVatOnlyTransactionQuery({ variables: { id: transaction.sourceId } });
          break;
        case DocumentType.SalesLedgerJournal:
          salesLedgerJournalTransactionQuery({ variables: { id: transaction.sourceId } });
          break;
        default:
          setNominalVat({
            nominalTransactions: [],
            vatTransactions: [],
          });
          break;
      }
    }
  }, [
    salesBadDebtTransactionQuery,
    salesCreditTransactionQuery,
    salesInvoiceTransactionQuery,
    salesReceiptTransactionQuery,
    salesRefundTransactionQuery,
    salesVatOnlyTransactionQuery,
    salesLedgerJournalTransactionQuery,
    transaction,
  ]);

  return [nominalVat, setNominalVat];
};

const SalesTransactionEnquiryForm = ({
  customer: propsCustomer,
  embedded,
  tourVisible,
}: {
  customer: Maybe<Pick<ICustomer, 'id' | 'currency'>>;
  embedded: boolean;
  tourVisible?: boolean;
}) => {
  const [customer, setCustomer] = useState<Maybe<Pick<ICustomer, 'id' | 'currency'>>>(null);
  const [outstanding, setOutstanding] = useValueState(false);
  const [balance, setBalance] = useState<Maybe<IOverdueBalance>>(null);
  const [salesTransaction, setSalesTransaction] = useState<Maybe<ISalesTransaction>>(null);
  const [allocation, setAllocation] = useState<Array<ISalesAllocationLine>>([]);
  const [nominalVat] = useNominalVatState(salesTransaction);
  const [showTabs, setShowTabs] = useState(false);
  const [stepOverride, setStepOverride] = useState<number | null>(null);

  const history = useHistory();

  useEffect(() => {
    setCustomer(propsCustomer);
  }, [propsCustomer]);

  const filters = useMemo<Array<IFilterInput>>(() => {
    const newFilters: Array<IFilterInput> = [];
    if (customer) {
      newFilters.push({
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'customer.id',
          value: `${customer.id}`,
        },
      });
    }
    if (outstanding) {
      newFilters.push({
        filterOperation: FilterOperation.NotEquals,
        filterParameters: {
          field: 'outstandingAmount',
          value: '0',
        },
      });
    }
    return newFilters;
  }, [outstanding, customer]);

  const setCustomerAndReset = useCallback((customer: ICustomer) => {
    setCustomer(customer);
    setSalesTransaction(null);
  }, []);

  const { data: balanceData } = useQuery<ISalesOverdueBalanceQueryResult, ISalesOverdueBalanceQueryVariables>(
    CustomerOverdueBalanceQuery,
    {
      skip: !customer,
      variables: { id: customer?.id },
    }
  );

  useEffect(() => {
    setBalance(balanceData?.data as IOverdueBalance);
  }, [balanceData]);

  const { data: salesTransactionAllocationData } = useQuery<
    ISalesTransactionAllocationsQueryResult,
    ISalesTransactionAllocationsQueryVariables
  >(SalesTransactionAllocationQuery, {
    variables: { id: salesTransaction?.id },
    fetchPolicy: 'no-cache',
    skip: !salesTransaction?.id,
  });

  useEffect(() => {
    let allocations: Array<ISalesAllocationLine> = [];
    if (salesTransactionAllocationData?.data?.salesAllocationLines) {
      allocations = salesTransactionAllocationData.data.salesAllocationLines.map((line) => {
        if (!line!.salesAllocation?.documentNumber) {
          return {
            ...line,
            salesAllocation: { ...line?.salesAllocation, documentNumber: 'Receipt' },
          } as ISalesAllocationLine;
        }
        return line as ISalesAllocationLine;
      });
    }
    setAllocation(allocations);
  }, [salesTransactionAllocationData]);

  const renderTransactionDetails = (base: boolean) => {
    if (!salesTransaction) return null;
    const { ValueField, DateField } = input;
    const vatable =
      salesTransaction.documentType === DocumentType.SalesInvoice ||
      salesTransaction.documentType === DocumentType.SalesCredit;
    const receipt =
      salesTransaction.documentType === DocumentType.SalesReceipt ||
      salesTransaction.documentType === DocumentType.SalesRefund;
    let {
      amount,
      outstandingAmount,
      goodsAmount,
      vatAmount,
      goodsAmountLessSettlementDiscount,
      vatAmountLessSettlementDiscount,
      discountAllowed,
    } = salesTransaction;
    if (base) {
      amount = salesTransaction.baseAmount;
      outstandingAmount = salesTransaction.baseOutstandingAmount;
      goodsAmount = salesTransaction.baseGoodsAmount;
      vatAmount = salesTransaction.baseVatAmount;
      goodsAmountLessSettlementDiscount = salesTransaction.baseGoodsAmountLessSettlementDiscount;
      vatAmountLessSettlementDiscount = salesTransaction.baseVatAmountLessSettlementDiscount;
      discountAllowed = salesTransaction.baseDiscountAllowed;
    }
    const nettAmount = goodsAmount - discountAllowed;
    return (
      <>
        {renderTransactionEnquiryHeader(salesTransaction)}
        <Form.Row>
          <Form.Fieldset cols={1} collapsed={true}>
            <SubHeader>{salesTransaction.note}</SubHeader>
          </Form.Fieldset>
        </Form.Row>
        <Form.Row>
          <Form.Fieldset cols={1 / 5}>
            <ValueField
              label="Total Amount"
              view={true}
              value={amount}
              decimals={2}
              data-testid={'transaction-details-total-amount'}
            />
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 5}>
            <ValueField
              label="Outstanding"
              view={true}
              value={outstandingAmount}
              decimals={2}
              data-testid={'transaction-details-outstanding-amount'}
            />
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 5}>
            <DateField
              label="Document Date"
              view={true}
              value={salesTransaction.documentDate}
              readOnly={true}
              data-testid={'transaction-details-document-date'}
              onChange={() => {}}
              onBlur={() => {}}
            />
          </Form.Fieldset>
          <Form.Fieldset cols={2 / 5}>
            <ValueField
              label="Document Reference"
              view={true}
              value={salesTransaction.documentReference || ''}
              type="text"
              data-testid={'transaction-details-document-reference'}
            />
          </Form.Fieldset>
        </Form.Row>
        {vatable ? (
          <>
            <Form.Row>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="Goods Amount"
                  view={true}
                  value={goodsAmount}
                  decimals={2}
                  data-testid={'transaction-details-goods-amount'}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="VAT Amount"
                  view={true}
                  value={vatAmount}
                  decimals={2}
                  data-testid={'transaction-details-vat-amount'}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="Posting Period"
                  view={true}
                  value={salesTransaction.financialPeriod?.name || ''}
                  type="text"
                  data-testid={'transaction-details-posting-period'}
                />
              </Form.Fieldset>
              {balance?.foreignCurrency ? (
                <Form.Fieldset cols={1 / 5}>
                  <ValueField
                    label="Exchange Rate"
                    view={true}
                    value={salesTransaction.exchangeRate}
                    decimals={5}
                    data-testid={'transaction-details-exchange-rate'}
                  />
                </Form.Fieldset>
              ) : null}
            </Form.Row>
            <Form.Row>
              <Form.Fieldset cols={1 / 5}>
                <DateField
                  label="Payment Due Date"
                  view={true}
                  value={salesTransaction.paymentDueDate}
                  readOnly={true}
                  data-testid={'transaction-details-payment-due-date'}
                  onChange={() => {}}
                  onBlur={() => {}}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 5}>
                <DateField
                  label="Settlement Date"
                  view={true}
                  value={salesTransaction.settlementDueDate}
                  readOnly={true}
                  data-testid={'transaction-details-settlement-due-date'}
                  onChange={() => {}}
                  onBlur={() => {}}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="Goods Less Settlement"
                  view={true}
                  value={goodsAmountLessSettlementDiscount}
                  decimals={2}
                  data-testid={'transaction-details-goods-less-settlement'}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="VAT Less Settlement"
                  view={true}
                  value={vatAmountLessSettlementDiscount}
                  decimals={2}
                  data-testid={'transaction-details-vat-less-settlement'}
                />
              </Form.Fieldset>
            </Form.Row>
          </>
        ) : null}
        {receipt ? (
          <>
            <Form.Row>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="Receipt/Refund Amount"
                  view={true}
                  value={nettAmount}
                  decimals={2}
                  data-testid={'transaction-details-receipt-refund-amount'}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="Discount"
                  view={true}
                  value={discountAllowed}
                  decimals={2}
                  data-testid={'transaction-details-discount'}
                />
              </Form.Fieldset>
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="Posting Period"
                  view={true}
                  value={salesTransaction.financialPeriod?.name || ''}
                  type="text"
                  data-testid={'transaction-details-posting-period-receipt'}
                />
              </Form.Fieldset>
            </Form.Row>
          </>
        ) : null}
        {!vatable && !receipt ? (
          <Form.Row>
            <Form.Fieldset cols={1 / 5}>
              <ValueField
                label="Posting Period"
                view={true}
                value={salesTransaction.financialPeriod?.name || ''}
                type="text"
                data-testid={'transaction-details-posting-period-other'}
              />
            </Form.Fieldset>
            {balance?.foreignCurrency ? (
              <Form.Fieldset cols={1 / 5}>
                <ValueField
                  label="Exchange Rate"
                  view={true}
                  value={salesTransaction.exchangeRate}
                  decimals={5}
                  data-testid={'transaction-details-exchange-rate-other'}
                />
              </Form.Fieldset>
            ) : null}
          </Form.Row>
        ) : null}
      </>
    );
  };

  const colors = balance && getBalanceColors(balance);
  const currencyIcon = getTransactionEnquiryCurrencyIcon(balance);
  const accountStatusIcon = getTransactionEnquiryAccountStatusIcon(balance);
  const foreignCurrency = balance && balance.foreignCurrency;
  return (
    <Form.FormContainer data-testid={'sales-transaction-enquiry-form'}>
      {tourVisible && (
        <GuidedTour
          tourId={'sales-transaction-enquiry-form-tour'}
          title={"We've Made Some Changes"}
          message={'The transaction details panel is now hidden until you click a row in the table'}
          activeStep={stepOverride}
          onTourStarted={() => {
            setStepOverride(0);
          }}
          steps={[
            {
              target: `//*[@data-testid="grid-container-view-/salesLedger/TransactionEnquiry"]`,
              primaryBtnText: 'Try it out',
              text: 'The table has been expanded to show more records. Selecting a transaction will show its details',
              primaryBtnAction: () => {
                setShowTabs(true);
                const rows = document.querySelectorAll(
                  '[data-testid^="datagrid-cell-"][data-testid$="-transactionNumber"]'
                );
                const row = Array.from(rows).find((row) => /\d/.test(row.getAttribute('data-testid') ?? ''));
                const clickEvent = new MouseEvent('click', {
                  view: window,
                  bubbles: true,
                  cancelable: true,
                });
                row?.dispatchEvent(clickEvent);
              },
            },
            {
              target: `//*[@id='st_tab_container']`,
              text: 'View the selected transaction details here',
            },
            {
              target: `//button[text()="Hide Tabs"]`,
              text: 'Hide the tab panel for more space to view transactions',
              primaryBtnText: 'Conclude tour',
            },
          ]}
        />
      )}

      <CurrencyContext.Provider value={{ currency: customer?.currency }}>
        {!embedded ? <TransactionEnquiryHeaderBlock onCancel={() => history.goBack()} /> : null}
        <Form.Form>
          <Spacer units={4} />
          <Form.Row>
            <Form.Fieldset cols={2 / 5}>
              {!embedded ? (
                <QueryDropdown
                  label="Customer"
                  {...Customer.dropdown}
                  onChange={setCustomerAndReset}
                  value={customer}
                  autoFocus={true}
                  data-testid={'customer'}
                />
              ) : null}
              <Form.Row>
                <Form.Fieldset cols={1 / 2}>
                  <input.Switch
                    label="Outstanding Only"
                    onChange={setOutstanding}
                    labelCols={6 / 12}
                    inputCols={6 / 12}
                    value={outstanding}
                  />
                </Form.Fieldset>
                {!embedded ? (
                  <>
                    <Form.Fieldset cols={1 / 4}>{currencyIcon}</Form.Fieldset>
                    <Form.Fieldset cols={1 / 4}>{accountStatusIcon}</Form.Fieldset>
                  </>
                ) : null}
              </Form.Row>
            </Form.Fieldset>
            {balance ? (
              <Form.Fieldset cols={3 / 5}>
                <Form.Row>
                  <Form.Fieldset cols={1 / 4} collapsed={true}>
                    <ValueCard
                      decimals={2}
                      label="Outstanding Amount"
                      labelPosition="top"
                      value={balance.outstandingAmount}
                      color={colors?.outstanding}
                      collapsed={true}
                      type={'currency'}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={1 / 4} collapsed={true}>
                    <ValueCard
                      decimals={2}
                      label="Overdue Amount"
                      labelPosition="top"
                      value={balance.overdueAmount}
                      color={colors?.overdue}
                      collapsed={true}
                      type={'currency'}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={1 / 4} collapsed={true}>
                    <ValueCard
                      decimals={2}
                      label="Overdue Invoice Amount"
                      labelPosition="top"
                      value={balance.overdueInvoiceAmount}
                      color={colors?.overdue}
                      collapsed={true}
                      type={'currency'}
                    />
                  </Form.Fieldset>
                  <Form.Fieldset cols={1 / 4} collapsed={true}>
                    <ValueCard
                      decimals={2}
                      label="Credit Limit"
                      labelPosition="top"
                      value={balance.creditLimit}
                      collapsed={true}
                      type={'currency'}
                    />
                  </Form.Fieldset>
                </Form.Row>
                <Form.Row>
                  <Form.Fieldset cols={2 / 3}>
                    {balance.outstandingAmount !== 0 ? (
                      <ValueBalanceBar
                        backgroundColor={colors?.barColor}
                        color={colors?.barProgressColor}
                        percent={colors?.overduePercent}
                      />
                    ) : null}
                  </Form.Fieldset>
                  <Form.Fieldset cols={1 / 3}>
                    {balance.creditLimit !== 0 ? (
                      <ValueBalanceBar
                        backgroundColor="#e1e1e1"
                        color={colors?.creditColor}
                        percent={colors?.creditPercent}
                        progress="left"
                      />
                    ) : null}
                  </Form.Fieldset>
                </Form.Row>
              </Form.Fieldset>
            ) : null}
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1}>
              {/* DynamicReportContext is here to clear out value set by DynamicReportContext in CreditControlWorkspace.
                  This forces it to default. Without this if you visit CreditControl > Form View > Dynamic Report
                  it'll try to print p4.lib.dto.salesLedger.CustomerCreditControl using SalesTransaction data.
               */}
              <DynamicReportContext.Provider value={{}}>
                {customer ? (
                  <SalesTransactionWorkspace
                    embeddedView={true}
                    additionalVariables={{ excludeCustomer: true }}
                    predefinedFilters={filters}
                    viewScreenName="/salesLedger/TransactionEnquiry"
                    contentHeight={
                      showTabs
                        ? getTransactionEnquiryGridHeight('main', embedded)
                        : getTransactionEnquiryGridHeight('main', embedded, 200)
                    }
                    onRowClick={(value: React.SetStateAction<Maybe<ISalesTransaction>>) => {
                      setSalesTransaction(value);
                      if (stepOverride !== null && stepOverride !== undefined) {
                        setStepOverride(1);
                      }
                      setShowTabs(true);
                    }}
                    ignoreContainerWidth={true}
                    noEdit={true}
                    customToolbarAction={<EmbeddedDynamicReport />}
                    contextKey={`salesTransactionEnquiryCustomer${customer?.id}`}
                  />
                ) : null}
              </DynamicReportContext.Provider>
            </Form.Fieldset>
          </Form.Row>
          <Form.Row>
            <Form.Fieldset cols={1} minHeight={300}>
              <div id={'st_tab_container'}>
                {customer && showTabs ? (
                  <Form.TabPanel customAction={<TabButton onClick={() => setShowTabs(false)} text={'Hide Tabs'} />}>
                    <Form.TabContent
                      id="general"
                      label={foreignCurrency ? `General (${balance.currency?.isoCode?.codeAlpha3})` : 'General'}
                    >
                      {renderTransactionDetails(false)}
                    </Form.TabContent>
                    <Form.TabContent id="basecurrency" label="General (GBP)" shouldRender={!!foreignCurrency}>
                      {renderTransactionDetails(true)}
                    </Form.TabContent>
                    <Form.TabContent id="nominal" label={foreignCurrency ? 'Nominal (GBP)' : 'Nominal'}>
                      <EnquiryNominalTransactions
                        transaction={salesTransaction}
                        nominalVat={nominalVat}
                        embedded={embedded}
                      />
                    </Form.TabContent>
                    <Form.TabContent id="vat" label={foreignCurrency ? 'VAT (GBP)' : 'VAT'}>
                      <EnquiryVatTransactions
                        transaction={salesTransaction}
                        nominalVat={nominalVat}
                        embedded={embedded}
                      />
                    </Form.TabContent>
                    <Form.TabContent id="allocation" label="Allocation">
                      <EnquiryAllocationTransactions
                        transaction={salesTransaction}
                        allocation={allocation}
                        embedded={embedded}
                      />
                    </Form.TabContent>
                  </Form.TabPanel>
                ) : null}
              </div>
            </Form.Fieldset>
          </Form.Row>
        </Form.Form>
      </CurrencyContext.Provider>
    </Form.FormContainer>
  );
};

export default SalesTransactionEnquiryForm;
